import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Badge,
  Stack,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Percent as PercentIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { Settings } from "lucide-react";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import BackBtn from "../widgets/BackBtn";
import { Wrench, Plus } from "lucide-react";

const ToolsList = () => {
  const [tools, setTools] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [searchName, setSearchName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    toolId: null,
    toolName: "",
  });

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchTools = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        limit,
        ...(searchName && { name: searchName }),
        ...(selectedCategory && { category: selectedCategory }),
      };
      const response = await axios.get(`${API_ENDPOINT}admin/get-tools-list`, {
        params,
        ...getHeader(),
      });
      setTools(response.data.data || []);
      setTotal(response.data.total || 0);
      // Extract unique categories
      const uniqueCats = [
        ...new Set(
          (response.data.data || [])
            .map((tool) => tool.category)
            .filter(Boolean)
        ),
      ];
      setCategories(uniqueCats);
    } catch (error) {
      console.error("Error fetching tools:", error);
      setError("Failed to fetch tools. Please try again.");
      setTools([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchName, selectedCategory]);

  // Single useEffect for API calls
  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchName, selectedCategory]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearchName(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleDeleteClick = (tool) => {
    setDeleteDialog({
      open: true,
      toolId: tool._id,
      toolName: tool.name,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.toolId) return;
    try {
      await axios.delete(
        `${API_ENDPOINT}admin/delete-tool/${deleteDialog.toolId}`,
        {
          ...getHeader(),
        }
      );
      await fetchTools(); // Refresh the list
      setDeleteDialog({ open: false, toolId: null, toolName: "" });
    } catch (error) {
      console.error("Error deleting tool:", error);
      setError("Failed to delete tool. Please try again.");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, toolId: null, toolName: "" });
  };

  const clearFilters = () => {
    setSearchName("");
    setSelectedCategory("");
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);
  const hasFilters = searchName || selectedCategory;

  // Helper to format price
  const formatPrice = (price) => {
    return `₹${parseFloat(price || 0).toFixed(2)}`;
  };

  // Helper to calculate discount percentage if not stored
  const calculateDiscount = (mrp, sellingPrice) => {
    if (!mrp || !sellingPrice || parseFloat(mrp) <= 0) return null;
    const discount =
      ((parseFloat(mrp) - parseFloat(sellingPrice)) / parseFloat(mrp)) * 100;
    return discount > 0 ? discount.toFixed(1) : null;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1600, margin: "0 auto" }}>
      <BackBtn />
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontWeight: 600, color: "primary.main" }}
          >
            Tools Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your tools inventory ({total} total tools)
          </Typography>
        </Box>
        <Stack direction={"row"} spacing={1}>
          <Button
            startIcon={<Settings size={18} />}
            variant="outlined"
            component={Link}
            to="/home/tools-settings"
          >
            Settings
          </Button>
          <Button
            startIcon={<Wrench size={18} />}
            variant="outlined"
            component={Link}
            to="/home/categories"
          >
            Manage Category
          </Button>
          <Button
            startIcon={<Plus size={18} />}
            variant="contained"
            component={Link}
            to="/home/add-tool"
            sx={{
              minWidth: 140,
              bgcolor: "primary.main",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
          >
            Add New Tool
          </Button>
        </Stack>
      </Box>
      {/* Filters Card */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6">Filters & Search</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <TextField
            label="Search by Name"
            value={searchName}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
              ),
            }}
            placeholder="Enter tool name..."
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Filter by Category"
              onChange={handleCategoryChange}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasFilters && (
            <Button variant="outlined" onClick={clearFilters} size="small">
              Clear Filters
            </Button>
          )}
        </Box>
        {/* Active Filters Display */}
        {hasFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {searchName && (
              <Chip
                label={`Search: "${searchName}"`}
                onDelete={() => setSearchName("")}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {selectedCategory && (
              <Chip
                label={`Category: ${selectedCategory}`}
                onDelete={() => setSelectedCategory("")}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Card>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {/* Table Section */}
      <Card>
        <TableContainer
          component={Paper}
          sx={{ position: "relative", minHeight: 400 }}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                zIndex: 10,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Loading tools...
                </Typography>
              </Box>
            </Box>
          )}
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: "grey.50" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Tool Information</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Stock
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  MRP
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Selling Price
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Discount
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Images</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tools.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {hasFilters
                        ? "No tools match your filters."
                        : "No tools found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tools.map((tool) => {
                  const mrp = parseFloat(tool.mrp || 0);
                  const sellingPrice = parseFloat(tool.sellingPrice || 0);
                  const storedDiscount =
                    tool.discount != null ? parseFloat(tool.discount) : null;
                  const calculatedDiscount = calculateDiscount(
                    mrp,
                    sellingPrice
                  );
                  const displayDiscount = storedDiscount ?? calculatedDiscount;
                  const images = tool.images || [];
                  return (
                    <TableRow
                      key={tool._id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "grey.50",
                        },
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            {tool.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {tool.description || "No description"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tool.category || "Uncategorized"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={tool.stock || 0}
                          color={
                            tool.stock > 10
                              ? "success"
                              : tool.stock > 0
                              ? "warning"
                              : "error"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatPrice(tool.mrp)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "success.main" }}
                        >
                          {formatPrice(tool.sellingPrice)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {displayDiscount != null ? (
                          <Chip
                            label={`${displayDiscount}% OFF`}
                            size="small"
                            color="error"
                            icon={<PercentIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              bgcolor: "error.light",
                              color: "error.contrastText",
                              fontWeight: 600,
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {images.length > 0 ? (
                          <Box
                            sx={{
                              position: "relative",
                              display: "inline-block",
                            }}
                          >
                            <img
                              src={images[0]}
                              alt={tool.name}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                            {images.length > 1 && (
                              <Badge
                                badgeContent={`+${images.length - 1}`}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    fontSize: "0.6rem",
                                    minWidth: 16,
                                    height: 16,
                                  },
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              bgcolor: "grey.200",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No Image
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <IconButton
                            component={Link}
                            to={`/home/${tool._id}/view-tool`}
                            size="small"
                            color="info"
                            title="View Details"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            component={Link}
                            to={`/home/${tool._id}/edit-tool`}
                            size="small"
                            color="primary"
                            title="Edit Tool"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(tool)}
                            size="small"
                            color="error"
                            title="Delete Tool"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {tools.length} of {total} tools
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Card>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the tool "
            <strong>{deleteDialog.toolName}</strong>"? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ToolsList;
